import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Paper, Grid  } from "@mui/material";
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from "recharts";

import _ from "lodash";
import axiosConfig from "../config.js";
import { getAllGroupKeys, normaliseGroupData, roundPriorityScore, transformAnalyticsData } from "../util.js";
import { FieldNameMappings } from "../enum.js";

/* Implemented Plan: 
 * 
 *  FIRST ANALYTIC: Priority Score Coefficient Breakdown Average
 *  A card at the top. It's a dropdown, that allows the surgeon to cycle between the different priority score factors for all patients. 
 *  This will be displayed with a !!!! "TinyBarChart" !!!!!
 *  
 *  For example, if the surgeon selects "pain level" then the bars would be drawn according to the enums, e.g. minimal, moderate, extreme (for this case)
 *  And so on and so forth. Like, four patients have minimal coefficient but like seventeen have moderate. 
 * 
 *  SECOND ANALYTIC: The Effects of Priority Score on Demographics 
 *  This will be displayed with a !!!! "StackedBarChart" !!!!!
 * 
 *  A card underneath. Again, a dropdown, but this time the surgeon can cycle through:
 *                - Age Groups.         Has variables of the age bins.          
 *                - Sex.                Has variables of F, and M. 
 *  These variables will be displayed side by side for each of the priority score coefficient labels. Am I doing a bad job at explaining this? Probably. https://recharts.org/en-US/examples/SimpleBarChart
 *  Look at that, and it'll probably be more clear. 

 *  THIRD (Provisional) ANALYTIC: SIMD vs Priority Score
 *  
 *  With all SIMD rankings (>6k) there is. 
 */



// NHS Scotland colour palette - fym "color"
// I KNOW THEY FORCE ME TO USE COLOR IN THE BLOODY RECHARTS I HATE RECHARTS
const nhsColours = {
  blue: '#005EB8',
  white: '#FFFFFF',
  darkBlue: '#003087',
  brightBlue: '#0072CE',
  lightBlue: '#d9e8ff',
  aquaBlue: '#00A9CE',
  black: '#231F20',
  darkGrey: '#425563',
  midGrey: '#768692',
  paleGrey: '#E8EDEE',
  yellow: '#FFB81C',
  green: '#009639',
  red: '#DA291C',
  purple: '#330072',
  pink: '#AE2573',
};


/* Need better intervals for the mock data. 
 * : 10-19 years (Pediatric/Adolescent) (Low incidence.)
 * : 20-39 years (Young Adults) (Low incidence.)
 * : 40-59 years (Middle-Aged) (Rising incidence.)
 * : 60-74 years (Young Seniors) (High incidence.)
 * : 75+ years (Elderly) (Highest incidence.) 
 * 
 * What we have:
 *  -  0-17
 *  - 18-24
 *  - 25-34
 *  - 35-44
 *  - 45-54
 *  - 55-64
 *  - 65-74
 *  - 75-84
 *  - 84+
 * 
 * i'm sure this is fiiinnneeeeee (it's hardcoded in the backend so good luck changing it if it's not)
 */

// fsdsdsfsdffffdsddddfdfdfds
const prioScoreFactors = ["painLevel", "adl", "mobility", "radioSeverity"];
const demographics = ["age", "sex"];

// 7q139iuhfioybgueyfouwefiuyq3eipygfiweghfiupgiytqefiuhqeuyotfgq398phfgyv
// comment above explains everything

// --- Component ---

export default function PatientsAnalytics() {
  
  // This is the variable for the priority score distribution bar chart. 
  const [priorityFactor, setPriorityFactor] = useState("painLevel");
  const [prioAnalytics, setPrioAnalytics] = useState({});


  // These are the variables for priority factor, vs the demographic. 
  const [demographic, setDemographic] = useState("age");
  // dg is short for demographic
  const [dgPriorityFactor, setDgPriorityFactor] = useState("painLevel");
  const [dgAnalytics, setDgAnalytics] = useState({});

  const [loading, setLoading] = useState(true);

  const [simdData, setSimdData] = useState({});
  const [simdGraphMax, setSimdGraphMax] = useState(20);

  // const chartColours = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f'];

  // fetches the analytics using axios, transform them so recharts understands them, and then normalise to keys (not needed)
  const fetchAnalytics = useCallback(async (dataSetter, field, groupBy = null) => {
    try {
      setLoading(true);

      const res = await axiosConfig.get(`/operations/analytics`, {
        params: {
          field,
          groupBy
        }
      });

      const data = res.data;
      let transformedData = transformAnalyticsData(data.analytics)
      // if the large list of [band]: 0 annoys you, comment out the line below
      transformedData = normaliseGroupData(transformedData);
      dataSetter(transformedData);

    } catch (err) {
      console.error(err);
      // setError("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSimdMetrics = useCallback(async () => {

    setLoading(true);

    let simdMap = [];
    
    for (let i = 1; i <= 10; ++i) {

      let filter = JSON.stringify({
        simd: i
      })

      const res = await axiosConfig.get("/operations/analytics/metrics", {
        params: {
          filter: filter
        }
      })

      simdMap.push({ simd: i, averagePriority: roundPriorityScore(await res.data.metrics.priorityScore) }); 
    }

    setSimdGraphMax(_.ceil((_.maxBy(simdMap, v => v.averagePriority).averagePriority*1.33*2), -1)/2);

    setSimdData(simdMap);
    setLoading(false);
    

  }, [setSimdData, setSimdGraphMax, setLoading]);

  // run the async ********
  useEffect(() => {
    fetchAnalytics(setPrioAnalytics, priorityFactor);
    fetchAnalytics(setDgAnalytics, dgPriorityFactor, demographic);
    fetchSimdMetrics();
  }, [fetchAnalytics, fetchSimdMetrics, setPrioAnalytics, setDgAnalytics, demographic, dgPriorityFactor, priorityFactor]);

  if (loading)
    return (<div>Loading...<br /></div>)

  return (
    <Box p={4} sx={{ backgroundColor: nhsColours.lightBlue }}>
      <Typography variant="h4" gutterBottom sx={{ color: nhsColours.darkBlue, fontWeight: 'bold' }}>
        Patient Population Analytics
      </Typography>

      <Grid container spacing={4}>
        {/* Priority Score Factor Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, backgroundColor: nhsColours.white }}>
            <Box display="flex" justifyContent="space-between" mb={3} alignItems="center">
              <Typography variant="h6" gutterBottom sx={{ color: nhsColours.darkBlue, my: "auto" }}>
                Priority Score Factor Distribution
              </Typography>

              <FormControl fullWidth sx={{ maxWidth: 300 }}>
                <InputLabel>Priority Factor</InputLabel>
                <Select
                  value={priorityFactor}
                  label="Priority Factor"
                  onChange={(e) => setPriorityFactor(e.target.value)}
                  sx={{ backgroundColor: nhsColours.white }}
                >
                  {prioScoreFactors.map((key) => (
                    <MenuItem key={key} value={key}>{FieldNameMappings[key]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={prioAnalytics}>
                <CartesianGrid strokeDasharray="3 3" stroke={nhsColours.midGrey} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: nhsColours.darkGrey }}
                />
                <YAxis tick={{ fill: nhsColours.darkGrey }} />
                <Tooltip />
                <Bar 
                  dataKey="count" 
                  fill={nhsColours.blue} 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* SIMD Rank vs Average Priority Score */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, backgroundColor: nhsColours.white, height: "100%", pt: "auto", pb: "auto" }}>
            <Typography variant="h6" gutterBottom sx={{ color: nhsColours.darkBlue }}>
              SIMD Decile vs Average Priority Score
            </Typography>

            <ResponsiveContainer width="100%" height={330}>
              <AreaChart data={simdData}>
                <defs>
                  <linearGradient id="colourPriority" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={nhsColours.blue} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={nhsColours.blue} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={nhsColours.midGrey} />
                <XAxis 
                  dataKey="simd" 
                  tick={{ fill: nhsColours.darkGrey }}
                />
                <YAxis 
                  // this horrible little chunk of code basically ceilings 133% of the average priority to the nearest 5
                  // then makes sure that the y axis is stepped in 5s, easy peasy
                  domain={[0, simdGraphMax]}
                  tickCount={simdGraphMax/5 + 1}
                  tick={{ fill: nhsColours.darkGrey }}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="averagePriority"
                  stroke={nhsColours.blue}
                  fill="url(#colourPriority)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Demographics vs Priority Factor - Full width */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, backgroundColor: nhsColours.white }}>
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
              <Typography variant="h6" sx={{ color: nhsColours.darkBlue, my: "auto" }}>
                Demographics vs Priority Factor
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Demographic</InputLabel>
                  <Select
                    value={demographic}
                    label="Demographic"
                    onChange={(e) => setDemographic(e.target.value)}
                    sx={{ backgroundColor: nhsColours.white }}
                  >
                    {demographics.map((key) => (
                      <MenuItem key={key} value={key}>{FieldNameMappings[key]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Priority Factor</InputLabel>
                  <Select
                    value={dgPriorityFactor}
                    label="Priority Factor"
                    onChange={(e) => setDgPriorityFactor(e.target.value)}
                    sx={{ backgroundColor: nhsColours.white }}
                  >
                    {prioScoreFactors.map((key) => (
                      <MenuItem key={key} value={key}>{FieldNameMappings[key]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={dgAnalytics}
                layout="vertical"
                margin={{ left: 100, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={nhsColours.midGrey} />
                <XAxis 
                  type="number" 
                  tick={{ fill: nhsColours.darkGrey }}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={120}
                  tick={{ fontSize: 12, fill: nhsColours.darkGrey }}
                />
                <Tooltip />
                <Legend />
                {getAllGroupKeys(dgAnalytics)
                  .map((key, index, arr) => (
                    <Bar 
                      key={key} 
                      dataKey={key} 
                      stackId="a" 
                      fill={[nhsColours.blue, nhsColours.green, nhsColours.red, nhsColours.purple][index % 4]}
                      name={key.replace(/[-\u2013]/, ' to ')}
                      radius={index === arr.length - 1 ? [0, 4, 4, 0] : 0}
                      // only round the last bar (this is broken if last bar does not exist... oops?)
                    />
                  ))}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
